import { Copy, LogOut, Users, Sparkles, Crown, Check, Gift, FileDown, Shield, Smartphone, Trash2, AlertTriangle } from "lucide-react";
import { useSession } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import InstallPwaButton from "@/components/InstallPwaButton";
import NotificationSettings from "@/components/NotificationSettings";
import PasswordInput from "@/components/PasswordInput";

const premiumFeatures = [
  "Storage illimitato per documenti",
  "Risposte AI prioritarie e più precise",
  "OCR avanzato su PDF",
  "Trascrizione automatica vocali",
  "Report PDF mensile della famiglia",
  "Esporta tutta la memoria in qualsiasi momento",
];

export default function SettingsPage() {
  const { user, family, members, logout, refreshSession } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgrading, setUpgrading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Check if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("🎉 Premium attivato! Benvenuto.");
          await refreshSession();
          setPolling(false);
          setSearchParams({});
          return;
        }
        if (data.status === "expired") {
          toast.error("Sessione scaduta");
          setPolling(false);
          setSearchParams({});
          return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else {
          toast.info("Verifica pagamento in corso...");
          setPolling(false);
        }
      } catch {
        setPolling(false);
      }
    };
    poll();
  }, []);

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Errore avvio pagamento");
      setUpgrading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(family?.invite_code || "");
    toast.success("Codice copiato!");
  };

  const copyReferral = () => {
    const link = `${window.location.origin}/auth?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Link referral copiato!");
  };

  const downloadReport = async () => {
    try {
      const response = await api.get("/family/report", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `family_report_${new Date().toISOString().slice(0, 7)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report scaricato");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Report PDF disponibile solo per Premium");
      } else {
        toast.error("Errore download");
      }
    }
  };

  const onLogout = () => {
    logout();
    navigate("/auth");
  };

  const confirmDelete = async () => {
    if (!deletePassword.trim()) {
      toast.error("Inserisci la password per confermare");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: deletePassword } });
      toast.success("Account eliminato. Arrivederci.");
      // Wipe session and go to landing
      logout();
      setTimeout(() => navigate("/"), 400);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore durante l'eliminazione";
      toast.error(msg);
      setDeleting(false);
    }
  };

  const isPremium = !!user?.is_premium;

  return (
    <div className="px-5 md:px-10 py-8 max-w-2xl mx-auto">
      <p className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">Profilo</p>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 mb-8">Impostazioni</h1>

      {polling && (
        <div className="bg-surface border border-subtle rounded-2xl p-4 mb-4 flex items-center gap-3">
          <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
          <span className="text-sm text-secondary-fg">Verifica del pagamento in corso...</span>
        </div>
      )}

      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full grid place-items-center text-white text-xl font-semibold relative"
               style={{ backgroundColor: user?.avatar_color }}>
            {user?.name?.[0]?.toUpperCase()}
            {isPremium && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-warm grid place-items-center border-2 border-app">
                <Crown className="w-3 h-3 text-app" />
              </div>
            )}
          </div>
          <div>
            <div className="font-display text-xl font-medium flex items-center gap-2">
              {user?.name}
              {isPremium && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-warm text-app font-semibold">PREMIUM</span>}
            </div>
            <div className="text-sm text-tertiary-fg">{user?.email}</div>
            <div className="text-xs text-brand uppercase tracking-widest mt-1">{user?.role}</div>
          </div>
        </div>
      </div>

      {/* Plan Card */}
      <div
        onClick={() => navigate("/pricing")}
        className="cursor-pointer relative overflow-hidden rounded-3xl p-6 mb-4 border bg-surface border-subtle hover:bg-elevated transition-colors"
        data-testid="settings-plan-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-brand grid place-items-center shrink-0">
            <Crown className="w-6 h-6 text-white" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-semibold">Il tuo piano</h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand text-white font-semibold uppercase tracking-widest">
                {user?.plan === "premium" ? "Premium" : user?.plan === "standard" ? "Standard" : "Free"}
              </span>
            </div>
            <p className="text-sm text-secondary-fg mt-1">
              {user?.plan_until
                ? `Attivo fino al ${new Date(user.plan_until).toLocaleDateString("it-IT")}`
                : "Vedi i piani disponibili e sblocca la memoria completa →"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-tertiary-fg" />
          <h2 className="font-display text-lg font-medium">{family?.name}</h2>
        </div>
        <div className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mb-2">Codice invito</div>
        <div className="flex items-center gap-2">
          <div className="flex-1 font-mono text-lg tracking-widest text-brand bg-elevated rounded-xl px-4 py-2.5">
            {family?.invite_code}
          </div>
          <button onClick={copy} data-testid="settings-copy-invite" className="btn-secondary !px-4">
            <Copy className="w-4 h-4" />
          </button>
        </div>

        <div className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mt-6 mb-2">Membri</div>
        <div className="space-y-2">
          {members?.map(m => (
            <div key={m.id} className="flex items-center gap-3 bg-elevated rounded-xl p-3">
              <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold text-sm" style={{ backgroundColor: m.avatar_color }}>
                {m.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{m.name} {m.id === user.id && <span className="text-xs text-tertiary-fg">(tu)</span>}</div>
                <div className="text-xs text-tertiary-fg">{m.email}</div>
              </div>
              <span className="text-xs text-tertiary-fg uppercase tracking-widest">{m.role}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-brand" />
          <h2 className="font-display text-lg font-medium">AI</h2>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed">
          Modello: <span className="text-primary-fg font-medium">GPT-5.2</span> via Emergent Universal LLM Key.
          L&apos;AI ha accesso a documenti, eventi e lista spesa della famiglia.
        </p>
      </div>

      {/* Referral */}
      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-sage" />
          <h2 className="font-display text-lg font-medium">Invita amici, guadagna premium</h2>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-4">
          Per ogni amico che si iscrive con il tuo codice <strong>e fa l&apos;upgrade</strong>, ricevi <span className="text-sage font-medium">+30 giorni Premium gratis</span>.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 font-mono text-base tracking-widest text-sage bg-elevated rounded-xl px-4 py-2.5">
            {user?.referral_code || "—"}
          </div>
          <button onClick={copyReferral} data-testid="settings-copy-referral" className="btn-secondary !px-4">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-tertiary-fg mt-2">
          Condividi il link: <code>{window.location.origin}/auth?ref={user?.referral_code}</code>
        </p>
      </div>

      {/* Report PDF (Premium) */}
      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <FileDown className="w-5 h-5 text-amber-warm" />
          <h2 className="font-display text-lg font-medium">Report famiglia</h2>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-4">
          Scarica il riepilogo PDF di tutta la memoria familiare (Premium).
        </p>
        <button onClick={downloadReport} data-testid="settings-download-report" className="btn-secondary w-full flex items-center justify-center gap-2">
          <FileDown className="w-4 h-4" /> Scarica report PDF
        </button>
      </div>

      {user?.is_admin && (
        <div onClick={() => navigate("/admin")} className="cursor-pointer bg-surface border border-brand/40 rounded-3xl p-6 mb-4 hover:bg-elevated transition-colors" data-testid="settings-admin-link">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand grid place-items-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-medium">Admin Dashboard</div>
              <div className="text-xs text-tertiary-fg">MRR, churn, utenti, coupon</div>
            </div>
          </div>
        </div>
      )}

      {/* PWA install */}
      <div className="bg-surface border border-subtle rounded-3xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Smartphone className="w-5 h-5 text-sage" />
          <h2 className="font-display text-lg font-medium">Installa sul telefono</h2>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-4">
          Aggiungi Family Brain alla schermata Home come una vera app. Zero spazio occupato in più.
        </p>
        <InstallPwaButton variant="button" />
      </div>

      {/* Notifiche push */}
      <NotificationSettings />

      <button
        onClick={onLogout}
        data-testid="settings-logout"
        className="w-full bg-surface border border-subtle rounded-2xl p-4 flex items-center justify-center gap-2 text-rose-warm hover:bg-elevated"
      >
        <LogOut className="w-4 h-4" /> Esci
      </button>

      {/* Danger Zone — Cancellazione account (Google Play compliance) */}
      <div data-testid="danger-zone" className="mt-8 bg-surface border border-rose-warm/40 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-rose-warm" />
          <h2 className="font-display text-lg font-medium text-rose-warm">Zona pericolosa</h2>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-4">
          L&apos;eliminazione dell&apos;account è <strong className="text-primary-fg">definitiva</strong>: verranno rimossi il tuo profilo,
          i tuoi documenti, messaggi, eventi e articoli creati. Non è possibile annullare l&apos;operazione.
        </p>
        <button
          onClick={() => setDeleteOpen(true)}
          data-testid="settings-delete-account"
          className="w-full bg-rose-warm/10 border border-rose-warm/60 text-rose-warm rounded-2xl p-3 flex items-center justify-center gap-2 hover:bg-rose-warm/20 transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Elimina account
        </button>
        <a
          href="/delete-account"
          data-testid="settings-delete-account-info-link"
          className="mt-3 block text-center text-xs text-tertiary-fg hover:text-primary-fg underline"
        >
          Maggiori informazioni sulla cancellazione
        </a>
      </div>

      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 bg-app/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteOpen(false)}
          data-testid="delete-account-modal-overlay"
        >
          <div
            className="bg-surface border border-rose-warm/50 rounded-3xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-account-modal"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-6 h-6 text-rose-warm" />
              <h3 className="font-display text-xl font-semibold text-rose-warm">Elimina account</h3>
            </div>
            <p className="text-sm text-secondary-fg leading-relaxed mb-4">
              Questa azione è <strong className="text-primary-fg">definitiva</strong> e non può essere annullata.
              Verranno eliminati: il tuo profilo, i tuoi documenti, eventi, articoli della spesa,
              messaggi personali con Memo AI e sottoscrizioni notifiche.
            </p>
            {family && members?.length > 1 && user?.id === family?.owner_id && (
              <p className="text-xs text-amber-warm bg-amber-warm/10 border border-amber-warm/30 rounded-xl p-3 mb-4">
                Sei il proprietario della famiglia. La proprietà verrà trasferita automaticamente al membro più anziano.
              </p>
            )}
            {members?.length === 1 && (
              <p className="text-xs text-rose-warm bg-rose-warm/10 border border-rose-warm/30 rounded-xl p-3 mb-4">
                Sei l&apos;unico membro. Verranno eliminati anche la famiglia e tutti i suoi dati condivisi.
              </p>
            )}
            <label className="block text-sm text-primary-fg font-medium mb-2">
              Conferma con la tua password
            </label>
            <PasswordInput
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmDelete(); }}
              placeholder="La tua password"
              autoFocus
              disabled={deleting}
              className="input-warm w-full mb-4 pl-10 pr-10"
              data-testid="delete-account-password"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setDeleteOpen(false); setDeletePassword(""); }}
                disabled={deleting}
                data-testid="delete-account-cancel"
                className="flex-1 bg-elevated border border-subtle rounded-full py-2.5 text-sm hover:bg-subtle disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || !deletePassword.trim()}
                data-testid="delete-account-confirm"
                className="flex-1 bg-rose-warm text-white rounded-full py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {deleting ? "Elimino..." : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
